
import React from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  useDroppable,
  closestCorners
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DebtRecord, Property, Portfolio, Team, TeamMember } from '../types';
import { GripVertical, Trash2, Layers, Plus, Home, ChevronRight, Building2, LogOut, Shield, ChevronDown, Landmark, Wallet, UserMinus, ReceiptText, Moon, Sun } from 'lucide-react';
import WrennenLogoMark from './WrennenLogoMark';
import { formatCurrency0 } from '../utils/format';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { arrayMove } from '@dnd-kit/sortable';

const isDev = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
const shouldLogPortfolioDnD = typeof window !== 'undefined' && isDev && window.localStorage.getItem('debug_portfolio_dnd') === '1';

interface SortablePropertyProps {
  id: string;
  property: Property;
  isActive: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

const SortableProperty = (props: SortablePropertyProps) => {
  const { id, property, isActive, onSelect, onDelete } = props;
  const propertyId = id.replace('property:', '');
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const { 
    attributes, 
    listeners, 
    setNodeRef, 
    transform, 
    transition, 
    isDragging 
  } = useSortable({ 
    id,
    data: {
      type: 'property',
      propertyId
    }
  });

  const style = { 
    transform: CSS.Transform.toString(transform), 
    transition, 
    zIndex: isDragging ? 100 : 'auto',
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 p-2.5 rounded-xl border transition-all cursor-pointer mb-2 ${
        isActive
          ? 'bg-slate-700 border-emerald-500/50 shadow-sm shadow-emerald-500/20'
          : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
      } ${isDragging ? 'ring-2 ring-emerald-500 shadow-xl shadow-emerald-500/20' : ''}`}
      onClick={() => onSelect(propertyId)}
    >
      <div
        {...listeners}
        {...attributes}
        className="cursor-grab active:cursor-grabbing text-slate-500 group-hover:text-slate-400 p-1"
      >
        <GripVertical size={14} />
      </div>
      <div className="flex-grow min-w-0">
        <div className={`text-[11px] font-bold truncate ${isActive ? 'text-emerald-400' : 'text-slate-200'}`}>
          {property.name || 'Unnamed Asset'}
        </div>
        <div className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">
          {formatCurrency0(property.inputs.purchasePrice)}
        </div>
      </div>
      {confirmingDelete ? (
        <div className="flex items-center gap-1">
          <button
            onClick={(e: any) => {
              e.stopPropagation();
              onDelete(propertyId);
            }}
            className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-rose-300"
          >
            Remove
          </button>
          <button
            onClick={(e: any) => {
              e.stopPropagation();
              setConfirmingDelete(false);
            }}
            className="rounded-lg border border-slate-700 bg-slate-900/80 px-2 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-slate-400"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={(e: any) => {
            e.stopPropagation();
            setConfirmingDelete(true);
          }}
          className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-500 hover:text-rose-500 rounded-lg transition-opacity"
          title="Delete property"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
};

interface PortfolioDropZoneProps {
  portfolio: Portfolio;
  allProperties: Record<string, Property>;
  isActive: boolean;
  isDraggingProperty: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRemoveAsset: (portfolioId: string, propertyId: string) => void;
}

const PortfolioDropZone = (props: PortfolioDropZoneProps) => {
  const { portfolio, allProperties, isActive, isDraggingProperty, onSelect, onDelete, onRemoveAsset } = props;
  const [isOpen, setIsOpen] = React.useState(true);
  const [confirmingPortfolioDelete, setConfirmingPortfolioDelete] = React.useState(false);
  const [confirmingAssetRemovalId, setConfirmingAssetRemovalId] = React.useState(null as string | null);
  const { isOver, setNodeRef } = useDroppable({
    id: `portfolio-drop:${portfolio.id}`,
    data: {
      type: 'portfolio-drop',
      portfolioId: portfolio.id
    }
  });

  const handleToggle = (e: any) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div
      ref={setNodeRef}
      className={`mb-3 rounded-[1.4rem] border transition-all group ${
        isOver
          ? 'border-emerald-400/60 bg-emerald-500/10 shadow-md shadow-emerald-500/10'
          : isDraggingProperty
            ? 'border-slate-600/90 bg-slate-900/55'
            : 'border-slate-800/80 bg-slate-900/60 hover:border-slate-700'
      }`}
    >
      <div
        onClick={() => onSelect(portfolio.id)}
        className={`flex items-center gap-2 px-3 py-3 cursor-pointer transition-colors ${
          isOver ? 'bg-emerald-500/10' : isActive ? 'bg-slate-800/70' : 'hover:bg-slate-800/30'
        }`}
      >
        <button
          onClick={handleToggle}
          className="rounded-lg p-1 hover:bg-slate-700 text-slate-400 transition-colors"
        >
          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        <div className={`rounded-lg border border-slate-800/80 bg-slate-950/80 p-1.5 ${isOver ? 'text-emerald-300' : 'text-slate-400'}`}>
          <Landmark size={14} />
        </div>

        <div className="flex-grow min-w-0">
          <div className="text-[12px] font-bold text-slate-100 truncate tracking-tight">
            {portfolio.name}
          </div>
          <div className="mt-1 text-[9px] font-black uppercase tracking-[0.18em] text-slate-500">
            {portfolio.propertyIds.length} assigned
          </div>
        </div>

        <div className="flex items-center gap-2 pr-1">
          <span className="rounded-full border border-slate-700 bg-slate-950/80 px-2 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">
            {portfolio.propertyIds.length}
          </span>
          {confirmingPortfolioDelete ? (
            <div className="flex items-center gap-1">
              <button
                onClick={(e: any) => {
                  e.stopPropagation();
                  onDelete(portfolio.id);
                }}
                className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-rose-300"
              >
                Remove
              </button>
              <button
                onClick={(e: any) => {
                  e.stopPropagation();
                  setConfirmingPortfolioDelete(false);
                }}
                className="rounded-lg border border-slate-700 bg-slate-900/80 px-2 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-slate-400"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={(e: any) => {
                e.stopPropagation();
                setConfirmingPortfolioDelete(true);
              }}
              className="rounded-lg p-1.5 text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-all opacity-0 group-hover:opacity-100"
              title="Delete portfolio"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="space-y-2 border-t border-slate-800/70 px-3 pb-3 pt-2">
          {portfolio.propertyIds.map((propId: any) => {
            const prop = allProperties[propId];
            if (!prop) return null;
            return (
              <div
                key={propId}
                className="group/asset flex items-center justify-between rounded-xl border border-slate-800/70 bg-slate-950/70 px-3 py-2 transition-all hover:border-slate-700 hover:bg-slate-950"
              >
                <span className="mr-2 truncate text-[11px] font-medium text-slate-200">
                  {prop.name}
                </span>
                {confirmingAssetRemovalId === propId ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e: any) => {
                        e.stopPropagation();
                        onRemoveAsset(portfolio.id, propId);
                        setConfirmingAssetRemovalId(null);
                      }}
                      className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-rose-300"
                    >
                      Remove
                    </button>
                    <button
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setConfirmingAssetRemovalId(null);
                      }}
                      className="rounded-lg border border-slate-700 bg-slate-900/80 px-2 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-slate-400"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setConfirmingAssetRemovalId(propId);
                    }}
                    className="p-0.5 text-slate-400 transition-colors hover:text-rose-500"
                    title="Remove from portfolio"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            );
          })}

          {portfolio.propertyIds.length === 0 && (
            <div className="py-4 text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest border border-dashed border-slate-700 rounded-xl">
              {isOver ? 'Release To Add' : 'Drop Properties Here'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface SidebarProps {
  themeMode: 'light' | 'dark';
  onToggleTheme: () => void;
  properties: Record<string, Property>;
  portfolios: Record<string, Portfolio>;
  debts: Record<string, DebtRecord>;
  propertyOrder: string[];
  activePropertyId: string | null;
  activeDebtScopePropertyId?: string | null;
  activePortfolioId: string | null;
  activeDebtId?: string | null;
  isWorkspaceReloading?: boolean;
  isDebtWorkspaceActive?: boolean;
  isTaxWorkspaceActive?: boolean;
  onSelectProperty: (id: string) => void;
  onSelectPortfolio: (id: string) => void;
  onSelectDebtWorkspace: () => void;
  onSelectTaxWorkspace: () => void;
  onSelectDebt: (debtId: string) => void;
  onDeleteProperty: (id: string) => void;
  onDeletePortfolio: (id: string) => void;
  onNewProperty: () => void;
  onCreatePortfolio: () => void;
  onViewPortfolio: () => void;
  onReorderProperties: (newOrder: string[]) => void;
  onAddToPortfolio: (propertyId: string, portfolioId: string) => 'added' | 'duplicate' | 'missing_portfolio' | 'missing_property';
  onRemoveFromPortfolio: (portfolioId: string, propertyId: string) => void;
  onGoHome?: () => void;
  user?: { uid?: string; displayName?: string; email?: string; photoURL?: string } | null;
  teams: Team[];
  activeTeamId: string | null;
  teamMembers: TeamMember[];
  teamStatus?: string | null;
  syncStatus?: {
    tone: 'success' | 'info' | 'error';
    label: string;
    detail: string;
  } | null;
  onSelectTeam: (teamId: string) => void;
  onAddTeamMember: (email: string) => Promise<void> | void;
  onRemoveTeamMember: (member: TeamMember) => Promise<void> | void;
  onLogout: () => void;
}

const Sidebar = (props: SidebarProps) => {
  const {
    themeMode, onToggleTheme,
    properties, portfolios, debts, propertyOrder, activePropertyId, activeDebtScopePropertyId, activePortfolioId, activeDebtId,
    isWorkspaceReloading,
    isDebtWorkspaceActive, isTaxWorkspaceActive,
    onSelectProperty, onSelectPortfolio, onSelectDebtWorkspace, onSelectTaxWorkspace, onSelectDebt, onDeleteProperty, onDeletePortfolio,
    onNewProperty, onCreatePortfolio, onViewPortfolio, onReorderProperties, onAddToPortfolio,
    onRemoveFromPortfolio, onGoHome, user, teams, activeTeamId, teamMembers, teamStatus, syncStatus, onSelectTeam, onAddTeamMember, onRemoveTeamMember, onLogout
  } = props;
  const [isDraggingProperty, setIsDraggingProperty] = React.useState(false);
  const [portfolioFeedback, setPortfolioFeedback] = React.useState('');
  const [inviteEmail, setInviteEmail] = React.useState('');
  const activeTeam = React.useMemo(
    () => teams.find((team) => team.id === activeTeamId) || null,
    [activeTeamId, teams]
  );

  const describeTeam = React.useCallback((team: Team) => {
    const isOwnedByCurrentUser = Boolean(user?.uid && team.ownerUserId === user.uid);
    if (team.isPersonal && isOwnedByCurrentUser) {
      return {
        optionLabel: `Your Personal Workspace${team.propertyCount > 0 ? ` · ${team.propertyCount} propert${team.propertyCount === 1 ? 'y' : 'ies'}` : ''}`,
        helperLabel: team.propertyCount > 0
          ? `Your personal workspace with ${team.propertyCount} saved propert${team.propertyCount === 1 ? 'y' : 'ies'}.`
          : 'Your personal workspace. It does not have any saved properties yet.'
      };
    }

    const accessLabel = team.role === 'owner' ? 'owner access' : `${team.role} access`;
    return {
      optionLabel: `${team.name}${team.propertyCount > 0 ? ` · ${team.propertyCount} propert${team.propertyCount === 1 ? 'y' : 'ies'}` : ''} · ${accessLabel}`,
      helperLabel: team.propertyCount > 0
        ? `Shared workspace with ${team.propertyCount} saved propert${team.propertyCount === 1 ? 'y' : 'ies'} and ${accessLabel}.`
        : `Shared workspace with ${accessLabel}, but no saved properties yet.`
    };
  }, [user?.uid]);

  const handleReorderInventory = (activePropId: string, overPropId: string) => {
    const oldIndex = propertyOrder.indexOf(activePropId);
    const newIndex = propertyOrder.indexOf(overPropId);
    if (oldIndex !== -1 && newIndex !== -1) {
      onReorderProperties(arrayMove(propertyOrder, oldIndex, newIndex));
    }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const clearFeedbackSoon = React.useCallback(() => {
    window.setTimeout(() => setPortfolioFeedback(''), 2200);
  }, []);
  const debtList = React.useMemo(() => {
    const propertyLookup = new Map<string, Property>();
    Object.values(properties).forEach((property) => {
      propertyLookup.set(property.id, property);
      if (property.supabaseId) {
        propertyLookup.set(property.supabaseId, property);
      }
    });

    const scopedDebts = (Object.values(debts) as DebtRecord[]).filter((debt) => {
      if (!activeDebtScopePropertyId) return true;
      const resolvedProperty = debt.propertyId ? propertyLookup.get(debt.propertyId) || null : null;
      if (!resolvedProperty) return false;
      return resolvedProperty.id === activeDebtScopePropertyId || resolvedProperty.supabaseId === activeDebtScopePropertyId;
    });

    const deduped = new Map<string, DebtRecord>();
    scopedDebts.forEach((debt) => {
      const resolvedProperty = debt.propertyId ? propertyLookup.get(debt.propertyId) || null : null;
      if (!resolvedProperty) return;

      const canonicalKey = debt.supabaseId
        || `${resolvedProperty.id}:${(debt.name || '').trim().toLowerCase()}:${(debt.lender || '').trim().toLowerCase()}`;
      const existing = deduped.get(canonicalKey);
      const debtTimestamp = debt.updatedAt || debt.createdAt || 0;
      const existingTimestamp = existing ? (existing.updatedAt || existing.createdAt || 0) : 0;

      if (!existing || debtTimestamp >= existingTimestamp) {
        deduped.set(canonicalKey, debt);
      }
    });

    return [...deduped.values()].sort((left, right) => (
      (right.updatedAt || right.createdAt || 0) - (left.updatedAt || left.createdAt || 0)
    ));
  }, [activeDebtScopePropertyId, debts, properties]);

  const handleDragStart = (event: DragStartEvent) => {
    const activeData = event.active.data.current;
    if (shouldLogPortfolioDnD) {
      console.info('[Portfolio DnD] drag start', {
        activeId: event.active.id,
        payload: activeData
      });
    }
    setIsDraggingProperty(activeData?.type === 'property');
    setPortfolioFeedback('');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setIsDraggingProperty(false);
    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;
    const overId = over.id.toString();
    const resolvedPortfolioId = overData?.portfolioId || (overId.startsWith('portfolio-drop:') ? overId.replace('portfolio-drop:', '') : null);

    if (shouldLogPortfolioDnD) {
      console.info('[Portfolio DnD] drag end', {
        activeId: active.id,
        overId,
        activeData,
        overData,
        resolvedPortfolioId
      });
    }

    if (activeData?.type === 'property' && resolvedPortfolioId) {
      const result = onAddToPortfolio(activeData.propertyId, resolvedPortfolioId);
      if (shouldLogPortfolioDnD) {
        console.info('[Portfolio DnD] add-to-portfolio result', {
          propertyId: activeData.propertyId,
          portfolioId: resolvedPortfolioId,
          result
        });
      }
      if (result === 'added') {
        setPortfolioFeedback('Property added to portfolio.');
      } else if (result === 'duplicate') {
        setPortfolioFeedback('That property is already in this portfolio.');
      } else if (result === 'missing_portfolio') {
        setPortfolioFeedback('Portfolio id is missing.');
      } else if (result === 'missing_property') {
        setPortfolioFeedback('Property id is invalid.');
      } else {
        setPortfolioFeedback('Could not save portfolio membership.');
      }
      clearFeedbackSoon();
      return;
    }

    if (active.id.toString().startsWith('property:') && over.id.toString().startsWith('property:')) {
      const activeIdStr = active.id.toString().replace('property:', '');
      const overIdStr = over.id.toString().replace('property:', '');
      handleReorderInventory(activeIdStr, overIdStr);
    }
  };

  const handleDragCancel = () => {
    setIsDraggingProperty(false);
  };

  return (
    <aside className="app-sidebar w-full lg:w-72 flex-shrink-0 bg-slate-900 border-r border-slate-800 h-screen overflow-hidden flex flex-col z-20">
      <div className="border-b border-slate-200 bg-white px-5 pb-5 pt-6">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onToggleTheme}
              className="inline-flex h-7 w-12 items-center rounded-full border border-slate-200 bg-slate-100 px-1 transition-colors hover:bg-slate-200"
              title={themeMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              aria-label={themeMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm transition-transform ${
                  themeMode === 'dark' ? 'translate-x-5' : 'translate-x-0'
                }`}
              >
                {themeMode === 'light' ? <Moon size={11} /> : <Sun size={11} />}
              </span>
            </button>
            <div className="group flex items-center gap-3 cursor-pointer" onClick={onGoHome}>
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-600 shadow-sm group-hover:scale-105 transition-transform">
                <WrennenLogoMark size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-sm font-semibold tracking-tight text-slate-900">Wrennen</h1>
                <p className="-mt-0.5 text-[11px] font-medium text-slate-500">Capital Systems</p>
              </div>
            </div>
          </div>
          {onGoHome && (
            <button
              onClick={onGoHome}
              className="rounded-2xl border border-slate-200 bg-white p-2.5 text-slate-500 transition-all hover:border-slate-300 hover:text-slate-700 hover:bg-slate-50"
              title="Go to Homepage"
            >
              <Home size={17} />
            </button>
          )}
        </div>

        <div className="space-y-3">
          <button
            onClick={onNewProperty}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-left transition-colors hover:bg-white hover:border-slate-300"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-900">Add Property</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">Create a new asset, then move into analysis after save.</p>
              </div>
              <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-500">
                New
              </span>
            </div>
          </button>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={onViewPortfolio}
              disabled={Object.keys(portfolios).length === 0}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <p className="text-sm font-medium text-slate-900">Portfolio</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">Open analytics</p>
            </button>
            <button
              onClick={onSelectDebtWorkspace}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:bg-slate-50"
            >
              <p className="text-sm font-medium text-slate-900">Debt Desk</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">Manage financing</p>
            </button>
          </div>
          <button
            onClick={onSelectTaxWorkspace}
            className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${isTaxWorkspaceActive ? 'border-slate-300 bg-slate-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-900">Tax Readiness</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">Organize year-end records and exports</p>
              </div>
              <ReceiptText size={14} className={isTaxWorkspaceActive ? 'text-slate-700' : 'text-slate-500'} />
            </div>
          </button>
        </div>
      </div>

      <div className="app-sidebar-scroll flex-1 min-h-0 overflow-y-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="px-4 pt-4 pb-6 space-y-8">
            {/* Master Inventory Section */}
            <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-4 px-2">
                <h2 className="text-sm font-medium text-slate-500 flex items-center gap-2">
                  <Building2 size={12} /> Asset Inventory
                </h2>
                <button onClick={onNewProperty} className="text-slate-500 hover:text-slate-700 p-1 bg-slate-50 rounded-lg transition-colors">
                  <Plus size={14} />
                </button>
              </div>

              <SortableContext
                items={propertyOrder.map(id => `property:${id}`)}
                strategy={verticalListSortingStrategy}
              >
              {(propertyOrder.map((id: any) => (
                properties[id] && React.createElement(SortableProperty, {
                  key: `property:${id}`,
                  id: `property:${id}`,
                  property: properties[id],
                  isActive: activePropertyId === id && !activePortfolioId,
                  onSelect: onSelectProperty,
                  onDelete: onDeleteProperty
                })
              )) as any)}
                {propertyOrder.length === 0 && (
                  <div className="py-8 text-center border border-dashed border-slate-700 rounded-xl text-[10px] text-slate-500 font-bold uppercase">
                    {isWorkspaceReloading ? 'Loading Team Properties' : 'No Properties Available'}
                  </div>
                )}
              </SortableContext>
            </section>

            {/* Portfolios Droppable Section */}
            <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-4 px-2">
                <h2 className="text-sm font-medium text-slate-500 flex items-center gap-2">
                  <Layers size={12} /> Portfolios
                </h2>
                <div className="flex items-center gap-2">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-medium text-slate-500">
                    {Object.keys(portfolios).length}
                  </span>
                  <button onClick={onCreatePortfolio} className="text-slate-500 hover:text-slate-700 p-1 bg-slate-50 rounded-lg transition-colors">
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              {portfolioFeedback && (
                <div className="mb-3 rounded-xl border border-slate-700/70 bg-slate-900/70 px-3 py-2 text-[10px] font-black uppercase tracking-[0.16em] text-slate-300">
                  {portfolioFeedback}
                </div>
              )}

              <div className="space-y-1.5">
                {(Object.values(portfolios).map((portfolio: Portfolio) =>
                  React.createElement(PortfolioDropZone, {
                    key: portfolio.id,
                    portfolio,
                    allProperties: properties,
                    isActive: activePortfolioId === portfolio.id,
                    isDraggingProperty,
                    onSelect: onSelectPortfolio,
                    onDelete: onDeletePortfolio,
                    onRemoveAsset: onRemoveFromPortfolio
                  })
                ) as any)}
                {Object.keys(portfolios).length === 0 && (
                  <div className={`rounded-xl border px-4 py-8 text-center transition-all ${isDraggingProperty ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' : 'border-dashed border-slate-700 text-slate-500'} text-[10px] font-bold uppercase italic`}>
                    {isWorkspaceReloading
                      ? 'Loading team portfolios'
                      : propertyOrder.length === 0
                      ? 'Create properties, then create a portfolio'
                      : isDraggingProperty
                        ? 'Create a portfolio before dropping properties'
                        : 'Create a portfolio, then drag properties here'}
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-[1.75rem] border border-slate-800/80 bg-slate-950/45 p-4">
              <div className="flex items-center justify-between mb-4 px-2">
                <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <Wallet size={12} /> Financing
                </h2>
                <span className="rounded-full border border-slate-800 bg-slate-950/80 px-2 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-slate-500">
                  {debtList.length}
                </span>
              </div>

              <button
                onClick={onSelectDebtWorkspace}
                className={`w-full rounded-2xl border px-4 py-3 text-left transition-all ${isDebtWorkspaceActive ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-slate-800 bg-slate-900/55 hover:border-slate-700'}`}
              >
                <p className="text-[11px] font-black text-white">Debt Desk</p>
                <p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-slate-500">Create debt and link it across assets</p>
              </button>

              <div className="mt-4 space-y-2">
                {debtList.map((debt: DebtRecord) => (
                  <button
                    key={debt.id}
                    onClick={() => onSelectDebt(debt.id)}
                    className={`w-full rounded-[1.35rem] border px-4 py-3 text-left transition-all ${
                      activeDebtId === debt.id && isDebtWorkspaceActive
                        ? 'border-emerald-500/40 bg-emerald-500/10'
                        : 'border-slate-800 bg-slate-950/70 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[11px] font-bold text-slate-100">{debt.name || 'Portfolio Loan'}</p>
                        <p className="mt-1 truncate text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">
                          {debt.lender || 'No lender'}
                        </p>
                      </div>
                      <span className="text-[10px] font-black text-slate-300">{formatCurrency0(debt.currentBalance)}</span>
                    </div>
                    <p className="mt-2 truncate text-[10px] text-slate-500">
                      {debt.propertyId ? `Linked to ${properties[debt.propertyId]?.name || Object.values(properties).find((property) => property.supabaseId === debt.propertyId)?.name || 'property'}` : 'Unassigned property'}
                    </p>
                  </button>
                ))}
                {debtList.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-700 px-4 py-6 text-center text-[10px] font-bold uppercase text-slate-500">
                    {isWorkspaceReloading ? 'Loading team debt' : 'No debt records yet'}
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        </DndContext>

        <div className="px-4 pb-6">
        {user && teams.length > 0 && (
          <div className="mb-4 rounded-[1.5rem] border border-slate-800/80 bg-slate-950/45 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Team Access</p>
              {activeTeam ? (
                <span className="rounded-full border border-slate-700 bg-slate-900/70 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">
                  {activeTeam.propertyCount} propert{activeTeam.propertyCount === 1 ? 'y' : 'ies'}
                </span>
              ) : null}
            </div>
            {syncStatus ? (
              <div className={`mt-3 rounded-2xl border px-3 py-3 ${
                syncStatus.tone === 'success'
                  ? 'border-emerald-500/30 bg-emerald-500/10'
                  : syncStatus.tone === 'error'
                    ? 'border-rose-500/30 bg-rose-500/10'
                    : 'border-cyan-500/30 bg-cyan-500/10'
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <p className={`text-[10px] font-black uppercase tracking-[0.18em] ${
                    syncStatus.tone === 'success'
                      ? 'text-emerald-300'
                      : syncStatus.tone === 'error'
                        ? 'text-rose-200'
                        : 'text-cyan-100'
                  }`}>
                    {syncStatus.label}
                  </p>
                  <span className={`h-2.5 w-2.5 rounded-full ${
                    syncStatus.tone === 'success'
                      ? 'bg-emerald-400'
                      : syncStatus.tone === 'error'
                        ? 'bg-rose-400'
                        : 'bg-cyan-300'
                  }`} />
                </div>
                <p className={`mt-1 text-[10px] ${
                  syncStatus.tone === 'success'
                    ? 'text-emerald-100/90'
                    : syncStatus.tone === 'error'
                      ? 'text-rose-100/90'
                      : 'text-cyan-100/90'
                }`}>
                  {syncStatus.detail}
                </p>
              </div>
            ) : null}
            <div className="mt-3 rounded-2xl border border-slate-800/70 bg-slate-900/45 p-3">
              <select
                value={activeTeamId || ''}
                onChange={(e: any) => onSelectTeam(e.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-[11px] font-bold text-white outline-none focus:border-emerald-500"
              >
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {describeTeam(team).optionLabel}
                  </option>
                ))}
              </select>
              {activeTeam ? (
                <p className="mt-3 text-[10px] leading-5 text-slate-400">
                  {describeTeam(activeTeam).helperLabel}
                </p>
              ) : null}
            </div>
            <div className="mt-3">
              <div className="mb-2 flex items-center justify-between px-1">
                <p className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">Members</p>
                <span className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-600">
                  {teamMembers.length}
                </span>
              </div>
              <div className="space-y-2">
                {teamMembers.slice(0, 4).map((member) => (
                  <div key={member.id} className="rounded-xl border border-slate-800/70 bg-slate-950/70 px-3 py-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="min-w-0 truncate text-[11px] font-bold text-slate-100">
                          {member.displayName || member.email || member.userId}
                        </p>
                        {member.email ? (
                          <p className="mt-1 truncate text-[10px] text-slate-500">
                            {member.email}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="rounded-full border border-slate-700 bg-slate-900/80 px-2 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-slate-400">
                          {member.role}
                        </span>
                        {member.userId !== user?.uid ? (
                          <button
                            onClick={() => void onRemoveTeamMember(member)}
                            className="rounded-lg border border-slate-800 bg-slate-900/80 p-1.5 text-slate-500 transition-all hover:border-rose-500/30 hover:bg-rose-500/10 hover:text-rose-300"
                            title={`Remove ${member.displayName || member.email || 'member'} from this workspace`}
                          >
                            <UserMinus size={12} />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e: any) => setInviteEmail(e.target.value)}
                placeholder="member@email.com"
                className="min-w-0 flex-1 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-[11px] font-medium text-white outline-none focus:border-emerald-500"
              />
              <button
                onClick={async () => {
                  if (!inviteEmail.trim()) return;
                  await onAddTeamMember(inviteEmail.trim());
                  setInviteEmail('');
                }}
                className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-300"
              >
                Add
              </button>
            </div>
            {teamStatus ? (
              <p className="mt-3 px-1 text-[10px] leading-5 text-slate-500">{teamStatus}</p>
            ) : null}
          </div>
        )}
        {user ? (
          <div className="flex items-center justify-between gap-3 px-2">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email || 'User')}`}
                alt={user.displayName || user.email || 'User'}
                className="w-8 h-8 rounded-full border border-slate-700"
                referrerPolicy="no-referrer"
              />
              <div className="min-w-0">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Logged in as</p>
                <p className="text-[10px] font-bold text-white truncate">{user.email || user.displayName || 'Authenticated user'}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="p-2 text-slate-500 hover:text-rose-500 hover:bg-slate-700 rounded-xl transition-all"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="w-full rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-center text-[11px] font-bold text-slate-300">
            Sign in to access the app
          </div>
        )}
        <div className="mt-4 flex items-center gap-2 text-slate-500 text-[9px] font-bold uppercase tracking-widest px-2">
          <ChevronRight size={12} /> Professional Analytics
        </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
